/**
 * What the browser refuses to upload for an import.
 *
 * Kept free of Vue and Firebase so it can be unit tested with plain node
 * (tests/unit/documentImportUploadLimits.test.js).
 *
 * The document goes straight from the browser to Firebase now, so nothing of
 * ours sees it until the worker fetches it and refuses anything over 10MB
 * part-way through the download. Checking here turns a long upload that ends
 * in failure into an instant, specific message.
 *
 * This is a courtesy, not a security control -- anything running in a browser
 * can be bypassed. The real gates are the bucket's Storage rules and
 * document_parser/source_url.py's streamed cap, which is why the number here
 * is deliberately the same 10MB.
 */

/** The same ceiling as source_url.MAX_DOCUMENT_BYTES on the server. */
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** What the parser can read, matching api.ALLOWED_MIME_TYPES. */
const ALLOWED_UPLOAD_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

/** Extensions to fall back on when a browser reports no type. */
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];

function looksAllowed(file) {
  const type = (file.type || '').toLowerCase();
  if (type) return ALLOWED_UPLOAD_TYPES.indexOf(type) !== -1;

  // Some browsers hand back an empty type, notably for PDFs picked from a
  // share sheet, so fall back to the name rather than rejecting a good file.
  const name = typeof file.name === 'string' ? file.name.toLowerCase() : '';
  const dot = name.lastIndexOf('.');
  if (dot === -1) return false;
  return ALLOWED_EXTENSIONS.indexOf(name.slice(dot + 1)) !== -1;
}

/**
 * The translation key for why this file cannot be uploaded, or null if it can.
 *
 * Size is reported ahead of type: it is the one the user can act on by
 * choosing a smaller file. A file object we cannot read at all is allowed
 * through, so the decision lands on the real gates rather than on a guess.
 */
function uploadRejectionKey(file) {
  if (!file) return null;

  if (typeof file.size === 'number' && file.size > MAX_UPLOAD_BYTES) {
    return 'importDocumentsTooLarge';
  }
  if (!looksAllowed(file)) return 'importDocumentsUnsupportedType';
  return null;
}

module.exports = {
  ALLOWED_UPLOAD_TYPES,
  MAX_UPLOAD_BYTES,
  uploadRejectionKey,
};
